'use client';

import { useEffect, useState } from 'react';
import MDS from '@fanduel/casino-mds';
import { getMdsEndpoint } from '@/utils/mds/get-mds-endpoint';

type MdsInstanceType = ReturnType<typeof MDS>;

let sharedInstance: MdsInstanceType | undefined;
let sharedUserId: string | undefined;

const ensureSharedInstance = (userId: string): MdsInstanceType | undefined => {
    if (sharedInstance && sharedUserId === userId) {
        return sharedInstance;
    }

    const endpoint = getMdsEndpoint();
    if (!endpoint) {
        return undefined;
    }

    // User changed (e.g. re-login): tear down the previous instance first.
    if (sharedInstance && sharedUserId !== userId) {
        sharedInstance.closeConnection();
        sharedInstance = undefined;
        sharedUserId = undefined;
    }

    const mds = MDS(endpoint, () => {});
    mds.initSocket(userId);
    sharedInstance = mds;
    sharedUserId = userId;
    return sharedInstance;
};

export const resetMdsInstance = () => {
    if (sharedInstance) {
        sharedInstance.closeConnection();
    }
    sharedInstance = undefined;
    sharedUserId = undefined;
};

export const useMdsInstance = (userId: string | undefined) => {
    const [instance, setInstance] = useState<MdsInstanceType | undefined>(undefined);

    useEffect(() => {
        if (!userId) {
            setInstance(undefined);
            return;
        }

        setInstance(ensureSharedInstance(userId));
        // No teardown on unmount: the shared instance must outlive mounts to keep realtime events on navigation.
    }, [userId]);

    return instance;
};
